import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Message, Spin } from "@/frontend/components/common";
import {
  teamService,
  tournamentFormatService,
  tournamentParticipantService,
  tournamentKnockoutSlotService,
} from "@/frontend/services";
import type {
  TeamResponseDTO,
  TournamentFormatResponseDTO,
} from "@/shared/dto";
import type { StageConfig } from "@/core/value-objects/StageConfig";
import { FaGripVertical, FaSave, FaTrophy } from "react-icons/fa";

interface KnockoutAssignmentBoardProps {
  tournamentId: string;
  seasonId: string;
  formatId: string;
  stage: StageConfig;
}

interface AssignableTeam {
  participantId: string;
  teamId: string;
  name: string;
  code: string;
  flag: string | null;
}

interface DuelBox {
  id: string;
  name: string;
  participantIds: string[];
}

interface ManualKnockoutSetup {
  stageOrder: number;
  matches: DuelBox[];
}

function getMatchCountForStage(stage: StageConfig): number {
  if (stage.structure?.knockoutMatchesCount && stage.structure.knockoutMatchesCount > 0) {
    return stage.structure.knockoutMatchesCount;
  }

  const round = stage.structure?.knockoutRound;
  const fallbackMap: Record<string, number> = {
    ROUND_OF_32: 16,
    ROUND_OF_16: 8,
    QUARTER_FINAL: 4,
    SEMI_FINAL: 2,
    FINAL: 1,
    THIRD_PLACE: 1,
  };
  if (round && fallbackMap[round] !== undefined) {
    return fallbackMap[round];
  }

  if (stage.structure?.knockoutTeamsCount && stage.structure.knockoutTeamsCount > 0) {
    return Math.ceil(stage.structure.knockoutTeamsCount / 2);
  }

  return 1;
}

function makeDuelBoxes(stage: StageConfig): DuelBox[] {
  const matchesCount = getMatchCountForStage(stage);
  return Array.from({ length: matchesCount }, (_, index) => ({
    id: `duel-${stage.order}-${index + 1}`,
    name: `Duelo ${index + 1}`,
    participantIds: [],
  }));
}

export function KnockoutAssignmentBoard({
  tournamentId,
  seasonId,
  formatId,
  stage,
}: KnockoutAssignmentBoardProps) {
  const [format, setFormat] = useState<TournamentFormatResponseDTO | null>(null);
  const [teams, setTeams] = useState<AssignableTeam[]>([]);
  const [duels, setDuels] = useState<DuelBox[]>(() => makeDuelBoxes(stage));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggingParticipantId, setDraggingParticipantId] = useState<string | null>(null);

  const fetchSetup = useCallback(async () => {
    setLoading(true);
    try {
      const [formatRes, participants] = await Promise.all([
        tournamentFormatService.getById(formatId),
        tournamentParticipantService.getAll({ tournamentId, seasonId }),
      ]);
      const loadedFormat = formatRes.success && formatRes.data ? formatRes.data : null;
      const teamIds = Array.from(
        new Set(participants.map((p) => p.teamId).filter((id): id is string => Boolean(id)))
      );
      const teamResponses = await Promise.all(teamIds.map((teamId) => teamService.getById(teamId)));
      const teamById = new Map<string, TeamResponseDTO>();

      teamResponses.forEach((res) => {
        if (res.success && res.data) teamById.set(res.data.id, res.data);
      });

      const nextTeams = participants
        .map((participant) => {
          if (!participant.teamId) return null;
          const team = teamById.get(participant.teamId);
          if (!team) return null;
          return {
            participantId: participant.id,
            teamId: participant.teamId,
            name: team.name,
            code: team.code,
            flag: team.country?.flag ?? null,
          } satisfies AssignableTeam;
        })
        .filter((team): team is AssignableTeam => team !== null)
        .sort((a, b) => a.name.localeCompare(b.name));

      const manualSetups = loadedFormat?.config?.manualKnockoutSetups as
        | Record<string, ManualKnockoutSetup>
        | undefined;
      const savedSetup = manualSetups?.[String(stage.order)];

      setFormat(loadedFormat);
      setTeams(nextTeams);
      const expectedDuelCount = getMatchCountForStage(stage);
      const savedDuelCount = savedSetup?.matches?.length ?? 0;

      if (savedSetup?.matches?.length && savedDuelCount === expectedDuelCount) {
        setDuels(savedSetup.matches);
      } else if (savedSetup?.matches?.length && savedDuelCount !== expectedDuelCount) {
        const merged = makeDuelBoxes(stage);
        for (let i = 0; i < Math.min(savedDuelCount, expectedDuelCount); i++) {
          if (savedSetup.matches[i]) {
            merged[i] = { ...merged[i], participantIds: savedSetup.matches[i].participantIds };
          }
        }
        setDuels(merged);
      } else {
        setDuels(makeDuelBoxes(stage));
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "No se pudieron cargar los duelos",
      });
    } finally {
      setLoading(false);
    }
  }, [formatId, seasonId, stage, tournamentId]);

  useEffect(() => {
    fetchSetup();
  }, [fetchSetup]);

  const assignedParticipantIds = useMemo(
    () => new Set(duels.flatMap((duel) => duel.participantIds)),
    [duels]
  );

  const availableTeams = useMemo(
    () => teams.filter((team) => !assignedParticipantIds.has(team.participantId)),
    [assignedParticipantIds, teams]
  );

  const handleDrop = (targetDuelId: string | null) => {
    const participantId = draggingParticipantId;
    if (!participantId) return;

    setDuels((prev) => {
      const withoutTeam = prev.map((duel) => ({
        ...duel,
        participantIds: duel.participantIds.filter((id) => id !== participantId),
      }));

      if (!targetDuelId) return withoutTeam;

      return withoutTeam.map((duel) => {
        if (duel.id !== targetDuelId) return duel;
        if (duel.participantIds.length >= 2) return duel;
        if (duel.participantIds.includes(participantId)) return duel;
        return { ...duel, participantIds: [...duel.participantIds, participantId] };
      });
    });
    setDraggingParticipantId(null);
  };

  const handleSave = async () => {
    if (!format) {
      Message.error({ content: "No se pudo cargar el formato para guardar duelos" });
      return;
    }

    setSaving(true);
    try {
      const manualKnockoutSetup: ManualKnockoutSetup = {
        stageOrder: stage.order,
        matches: duels,
      };
      const res = await tournamentFormatService.update(formatId, {
        name: format.name,
        sport: format.sport,
        config: {
          ...format.config,
          manualKnockoutSetups: {
            ...((format.config?.manualKnockoutSetups as Record<string, ManualKnockoutSetup> | undefined) ?? {}),
            [String(stage.order)]: manualKnockoutSetup,
          },
        },
      });

      if (res.success && res.data) {
        setFormat(res.data);
        try {
          await tournamentKnockoutSlotService.syncManual(seasonId, stage.order);
        } catch (syncErr) {
          console.warn("Sync de slots knockout falló (no crítico):", syncErr);
        }
        Message.success({ content: "Duelos guardados correctamente" });
      } else {
        Message.error({ content: res.error ?? "No se pudieron guardar los duelos" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "No se pudieron guardar los duelos",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderTeamCard = (team: AssignableTeam) => (
    <div
      key={team.participantId}
      draggable
      onDragStart={() => setDraggingParticipantId(team.participantId)}
      onDragEnd={() => setDraggingParticipantId(null)}
      className={`flex cursor-grab items-center gap-2 rounded border bg-white px-3 py-2 text-sm shadow-sm transition ${
        draggingParticipantId === team.participantId
          ? "border-amber-300 opacity-60"
          : "border-gray-200 hover:border-amber-300"
      }`}
    >
      <FaGripVertical className="shrink-0 text-gray-300" />
      {team.flag && <img src={team.flag} alt="" className="h-3.5 w-5 rounded object-cover" />}
      <span className="min-w-0 flex-1 truncate font-medium text-gray-700">{team.name}</span>
      <span className="text-xs text-gray-400">{team.code}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Spin size="large" tip="Cargando duelos..." />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <FaTrophy className="text-amber-500" />
          <h4 className="font-semibold text-gray-700">Duelos</h4>
        </div>
        <Button type="primary" icon={<FaSave />} onClick={handleSave} loading={saving} size="small">
          Guardar duelos
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_300px]">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {duels.map((duel) => (
            <div
              key={duel.id}
              className="min-h-40 rounded-md border border-gray-200 bg-gray-50"
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDrop(duel.id)}
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                <span className="text-sm font-semibold text-gray-700">{duel.name}</span>
                <span className="text-xs text-gray-500">{duel.participantIds.length}/2</span>
              </div>
              <div className="space-y-2 p-2">
                {duel.participantIds.length === 0 ? (
                  <div className="flex h-20 items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-400">
                    Suelta 2 equipos aquí
                  </div>
                ) : (
                  duel.participantIds
                    .map((participantId) => teams.find((team) => team.participantId === participantId))
                    .filter((team): team is AssignableTeam => Boolean(team))
                    .map(renderTeamCard)
                )}
              </div>
            </div>
          ))}
        </div>

        <div
          className="min-h-48 rounded-md border border-gray-200 bg-slate-50"
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => handleDrop(null)}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <span className="text-sm font-semibold text-gray-700">Equipos disponibles</span>
            <span className="text-xs text-gray-500">{availableTeams.length}</span>
          </div>
          <div className="max-h-96 space-y-2 overflow-y-auto p-2">
            {availableTeams.length === 0 ? (
              <div className="flex h-20 items-center justify-center rounded border border-dashed border-gray-300 text-xs text-gray-400">
                Sin equipos disponibles
              </div>
            ) : (
              availableTeams.map(renderTeamCard)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
